import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronDown, Plus, Search, UserRound } from 'lucide-react';
import { api } from '../lib/api';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';
import { SelectionActionBar, SelectionCheckbox, useTableSelection } from '../components/TableSelection';

const STATUS_FILTERS = [
  { v: '', label: 'All' },
  { v: 'verified', label: 'Verified' },
  { v: 'needs_review', label: 'Action required' },
  { v: 'pending', label: 'Pending' },
  { v: 'rejected', label: 'Rejected' },
  { v: 'draft', label: 'Draft' },
  { v: 'revoked', label: 'Revoked' },
];

const badgeStyles = {
  verified: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  needs_review: 'bg-amber-50 text-amber-800 border-amber-300',
  needs_action: 'bg-amber-50 text-amber-800 border-amber-300',
  pending: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  draft: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  created: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  not_received: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  rejected: 'bg-red-50 text-red-700 border-red-300',
  revoked: 'bg-red-50 text-red-700 border-red-300',
};

function fmt(value) {
  return value || '-';
}

function fmtDate(value) {
  return value ? new Date(value).toLocaleString() : '';
}

function StatusBadge({ status }) {
  const value = status || 'draft';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 uppercase tracking-widest text-[10px] font-mono border ${badgeStyles[value] || badgeStyles.draft}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"/>
      {value.replaceAll('_', ' ')}
    </span>
  );
}

function MethodBadge({ representativeCount = 0 }) {
  const count = Number.isFinite(Number(representativeCount)) ? Number(representativeCount) : 0;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-flex h-6 items-center gap-1.5 rounded-full px-2 py-0.5 uppercase tracking-widest text-[10px] font-mono border text-zinc-500 border-zinc-200">
        <Building2 className="w-3 h-3" strokeWidth={1.8}/>
        KYB
      </span>
      <span className="inline-flex h-6 min-w-8 items-center justify-center gap-1.5 rounded-full px-2 py-0.5 uppercase tracking-widest text-[10px] font-mono border text-zinc-500 border-zinc-200" title="Representatives">
        {count}
        <UserRound className="w-3 h-3" strokeWidth={1.8}/>
      </span>
    </span>
  );
}

export default function BusinessVerifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (status) params.status = status;
    if (q.trim()) params.q = q.trim();
    const { data } = await api.get('/business-verifications', { params });
    setItems(data.items || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const counts = useMemo(() => {
    return items.reduce((acc, item) => {
      const displayStatus = item.effective_status || item.status;
      if (displayStatus === 'verified') acc.verified++;
      else if (displayStatus === 'needs_review') acc.needs_review++;
      else if (['rejected', 'revoked'].includes(displayStatus)) acc.failed++;
      else acc.pending++;
      return acc;
    }, { verified: 0, needs_review: 0, pending: 0, failed: 0 });
  }, [items]);

  const tableSelection = useTableSelection(items);
  const selectedStatusLabel = STATUS_FILTERS.find((filter) => filter.v === status)?.label || 'All';
  const statusButtonLabel = status ? `Status: ${selectedStatusLabel}` : 'Status';

  return (
    <div data-testid="business-verifications-page">
      <VerificationBreadcrumb rootLabel="Businesses" rootTo="/dashboard/businesses" />

      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Verifications</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Businesses</h1>
        </div>
        <button onClick={() => navigate('/dashboard/businesses/new')}
          className="inline-flex items-center gap-2 bg-black text-white text-sm px-3 py-2 rounded-sm hover:bg-zinc-800">
          <Plus className="w-4 h-4" strokeWidth={2}/> New verification
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { k: 'verified', label: 'Verified', v: counts.verified },
          { k: 'needs_review', label: 'Action required', v: counts.needs_review },
          { k: 'pending', label: 'Pending', v: counts.pending },
          { k: 'failed', label: 'Failed', v: counts.failed },
        ].map((card) => (
          <div key={card.k} className="border border-zinc-200 rounded-sm bg-white p-4">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{card.label}</div>
            <div className="font-display text-2xl font-bold mt-1">{card.v}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 justify-between mb-4">
        <div className="relative" onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setStatusMenuOpen(false);
        }}>
          <button type="button" onClick={() => setStatusMenuOpen((open) => !open)}
            className="h-[34px] inline-flex items-center gap-2 border border-zinc-200 rounded-sm bg-white px-3 text-[10px] font-medium uppercase tracking-widest text-zinc-500 hover:border-zinc-300">
            {statusButtonLabel}
            <ChevronDown className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5}/>
          </button>
          {statusMenuOpen && (
            <div className="absolute left-0 top-[calc(100%+4px)] z-20 w-44 border border-zinc-200 rounded-sm bg-white py-1">
              {STATUS_FILTERS.map((filter) => (
                <button key={filter.v || 'all'} type="button"
                  onClick={() => { setStatus(filter.v); setStatusMenuOpen(false); }}
                  className={`block w-full text-left px-3 py-2 text-[10px] font-medium uppercase tracking-widest ${status === filter.v ? 'text-zinc-500' : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50'}`}>
                  {filter.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="w-full sm:w-80 h-[34px] flex items-center gap-2 border border-zinc-200 rounded-sm bg-white px-3">
          <Search className="w-4 h-4 text-zinc-400" strokeWidth={1.5}/>
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            className="flex-1 h-full text-[10px] font-medium uppercase tracking-widest bg-transparent text-zinc-500 focus:outline-none placeholder:text-zinc-400"
            placeholder="Search businesses"/>
        </div>
      </div>

      <SelectionActionBar count={tableSelection.selectedCount} onClear={tableSelection.clearSelection}/>
      <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-xs uppercase tracking-widest text-zinc-500">
              <th className="w-10 px-4 py-3 align-middle font-medium">
                <SelectionCheckbox
                  checked={tableSelection.allVisibleSelected}
                  indeterminate={tableSelection.someVisibleSelected && !tableSelection.allVisibleSelected}
                  onCheckedChange={tableSelection.toggleAllVisible}
                  ariaLabel="Select all visible business verification rows"
                />
              </th>
              <th className="text-left px-4 py-3 font-medium">Business</th>
              <th className="text-left px-4 py-3 font-medium">Identifier</th>
              <th className="text-left px-4 py-3 font-medium">Method</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Created</th>
              <th className="text-left px-4 py-3 font-medium">Updated</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-12 text-sm text-zinc-500">Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-sm text-zinc-500">No business verifications yet.</td></tr>
            ) : items.map((item) => {
              const displayStatus = item.effective_status || item.status;
              const isSelected = tableSelection.isSelected(item.id);
              return (
                <tr key={item.id} onClick={() => navigate(`/dashboard/businesses/${item.id}`)}
                  className={`border-b border-zinc-100 cursor-pointer ${isSelected ? 'bg-zinc-50/80' : 'hover:bg-zinc-50/60'}`}>
                  <td className="w-10 px-4 py-4 align-middle">
                    <SelectionCheckbox
                      checked={isSelected}
                      onCheckedChange={() => tableSelection.toggleRow(item.id)}
                      ariaLabel={`Select ${item.business_name || 'business verification'}`}
                    />
                  </td>
                  <td className="px-4 py-4 align-middle">
                    <div className="flex items-center gap-2 min-w-0">
                      <Building2 className="w-4 h-4 text-zinc-400 shrink-0" strokeWidth={1.5}/>
                      <div className="font-medium truncate min-w-0 max-w-[12rem]" title={item.business_name}>{item.business_name}</div>
                    </div>
                  </td>
                  <td className="px-4 py-4 align-middle">
                    <div className="min-w-0">
                      <div className="font-mono text-xs text-zinc-500 truncate">{fmt(item.enterprise_number)}</div>
                      <div className="mt-1 text-xs text-zinc-500 leading-4">Enterprise number</div>
                    </div>
                  </td>
                  <td className="px-4 py-4 align-middle"><MethodBadge representativeCount={item.representative_count}/></td>
                  <td className="px-4 py-4 align-middle"><StatusBadge status={displayStatus}/></td>
                  <td className="px-4 py-4 align-middle font-mono text-xs text-zinc-500">{fmtDate(item.created_at)}</td>
                  <td className="px-4 py-4 align-middle font-mono text-xs text-zinc-500">{fmtDate(item.updated_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
